import { HttpErrorResponse } from '@angular/common/http';

export interface AdminResponseHeader {
  returnCode: string;
  returnDesc: string;
}

export interface AdminResponse<T> {
  responseHeader: AdminResponseHeader;
  responseBody: T;
}

export class AdminApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly httpStatus?: number,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export function unwrapAdminResponse<T>(response: AdminResponse<T>): T {
  if (response.responseHeader.returnCode !== 'EX0000') {
    throw new AdminApiError(
      response.responseHeader.returnCode,
      adminErrorMessage(response.responseHeader.returnCode, response.responseHeader.returnDesc),
    );
  }
  return response.responseBody;
}

export function normalizeAdminError(error: unknown): AdminApiError {
  if (error instanceof AdminApiError) return error;
  if (error instanceof HttpErrorResponse) {
    const wrapped = error.error as Partial<AdminResponse<unknown>> | null;
    const code = wrapped?.responseHeader?.returnCode;
    const description = wrapped?.responseHeader?.returnDesc;
    if (code) return new AdminApiError(code, adminErrorMessage(code, description), error.status);
    if (error.status === 401) return new AdminApiError('EX4010', '登入狀態已失效，請重新驗證。', 401);
    if (error.status === 403) return new AdminApiError('EX4030', '目前帳號沒有執行此操作的權限。', 403);
    if (error.status === 0) return new AdminApiError('NETWORK_ERROR', '無法連線至後台服務，請稍後再試。', 0);
  }
  return new AdminApiError('UNEXPECTED_ERROR', '發生未預期的錯誤，請稍後再試。');
}

export function adminErrorMessage(code: string, fallback = ''): string {
  const messages: Record<string, string> = {
    EX4010: '登入狀態已失效，請重新驗證。',
    EX4013: '此帳號不在管理者白名單中，或帳號已停用。',
    EX4014: 'Cloudflare Access 驗證已失效，請重新登入。',
    EX4030: '目前帳號沒有執行此操作的權限。',
    EX4040: '找不到指定活動。',
    EX4041: '找不到指定管理者。',
    EX4093: '此 Email 已存在於管理者白名單。',
    EX4094: '不可停用目前登入中的帳號。',
    EX4095: '不可停用或降權最後一位啟用中的 ROOT。',
    EX4096: '活動已被刪除，資料已重新載入。',
    EX5030: 'Cloudflare Access 白名單同步失敗，請保留資料後重試。',
    EX9909: fallback || '查詢條件或欄位格式不正確。',
  };
  return messages[code] || fallback || '後台服務處理失敗。';
}
